import { ChangeEvent, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { HelpText } from 'src/components/atoms';
import { DatePicker } from 'src/components/molecules';
import {
  CaseScreenHeaderDetailsColumnNew,
  HeaderSectionTitle,
  HeaderSectionTitleContainer,
} from 'src/components/organismsLegacy/CaseScreenHeader/CaseScreenHeader.styles';
import { caseDateUpdated } from 'src/components/pages/Case/actions/caseDateUpdated';
import { useUpdateCaseAttributesMutation } from 'src/graphql';
import { AppDispatch } from 'src/store';
import { DateType, FormState } from 'src/types';
import { isValidDate } from 'src/utils/time';
import {
  CaseDateRow,
  CaseDatesPanelContainer,
  DatePickerContainer,
} from './CaseDatesPanel.styles';
import { caseDatesPanel } from './CaseDatesPanel.vm';

export const CaseDatesPanel = () => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const {
    caseId,
    companyCreatedAt,
    companyCreatedAtMax,
    companyCreatedAtMin,
    readAt,
    readAtMax,
    readAtMin,
    investigatingAt,
    investigatingAtMax,
    investigatingAtMin,
    closedAt,
    closedAtMax,
    closedAtMin,
    canEditClosedAtDate,
    canEditCreatedAtDate,
    canEditInvestigatingAtDate,
    canEditReadAtDate,
  } = useSelector(caseDatesPanel);

  const [invalidDates, setInvalidDates] = useState({
    companyCreatedAt: null,
    readAt: null,
    investigatingAt: null,
    closedAt: null,
  });
  const updateCaseAttributes = useUpdateCaseAttributesMutation();

  const onDateChange =
    (dateType: DateType) => (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      const isValueInvalid =
        !e.target.checkValidity() ||
        (Boolean(inputValue) && !isValidDate(inputValue, 'YYYY-MM-DD'));

      setInvalidDates((prev) => ({
        ...prev,
        [dateType]: isValueInvalid ? inputValue : null,
      }));

      if (isValueInvalid) {
        return;
      }

      dispatch(
        caseDateUpdated({
          caseId,
          date: inputValue ?? null,
          dateType,
          queryClient,
          mutateUpdateCaseAttributes: updateCaseAttributes.mutateAsync,
        }),
      );
    };

  return (
    <CaseScreenHeaderDetailsColumnNew padding={'0 20px 0 0'}>
      <HeaderSectionTitleContainer>
        <HeaderSectionTitle>Case dates</HeaderSectionTitle>
      </HeaderSectionTitleContainer>
      <CaseDatesPanelContainer>
        <CaseDateRow>
          <DatePickerContainer>
            <DatePicker
              value={invalidDates.companyCreatedAt ?? companyCreatedAt ?? ''}
              min={companyCreatedAtMin}
              max={companyCreatedAtMax}
              disabled={!canEditCreatedAtDate}
              required={readAt !== undefined}
              width={'auto'}
              onChange={onDateChange(DateType.CompanyCreatedAt)}
              error={Boolean(invalidDates.companyCreatedAt)}
              data-testid="case-created-at-date-picker"
              label={'Created'}
            />
            {invalidDates.companyCreatedAt !== null && (
              <HelpText
                data-testid="case-created-at-date-error"
                state={FormState.ERROR}
                helpText="Please enter a valid date"
              />
            )}
          </DatePickerContainer>

          <DatePickerContainer>
            <DatePicker
              value={invalidDates.readAt ?? readAt ?? ''}
              min={readAtMin}
              max={readAtMax}
              disabled={!canEditReadAtDate}
              required={investigatingAt !== undefined}
              width={'auto'}
              onChange={onDateChange(DateType.ReadAt)}
              error={Boolean(invalidDates.readAt)}
              data-testid="case-read-at-date-picker"
              label={'Read'}
            />
            {invalidDates.readAt !== null && (
              <HelpText
                data-testid="case-read-at-date-error"
                state={FormState.ERROR}
                helpText="Please enter a valid date"
              />
            )}
          </DatePickerContainer>
        </CaseDateRow>
        <CaseDateRow>
          <DatePickerContainer>
            <DatePicker
              value={invalidDates.investigatingAt ?? investigatingAt ?? ''}
              min={investigatingAtMin}
              max={investigatingAtMax}
              disabled={!canEditInvestigatingAtDate}
              required={closedAt !== undefined}
              width={'auto'}
              onChange={onDateChange(DateType.InvestigatingdAt)}
              error={Boolean(invalidDates.investigatingAt)}
              data-testid="case-investigating-at-date-picker"
              label={'Investigating'}
            />
            {invalidDates.investigatingAt !== null && (
              <HelpText
                data-testid="case-investigating-at-date-error"
                state={FormState.ERROR}
                helpText="Please enter a valid date"
              />
            )}
          </DatePickerContainer>

          <DatePickerContainer>
            <DatePicker
              value={invalidDates.closedAt ?? closedAt ?? ''}
              min={closedAtMin}
              max={closedAtMax}
              disabled={!canEditClosedAtDate}
              onChange={onDateChange(DateType.ClosedAt)}
              width={'auto'}
              error={Boolean(invalidDates.closedAt)}
              data-testid="case-closed-at-date-picker"
              label={'Closed'}
            />
            {invalidDates.closedAt !== null && (
              <HelpText
                data-testid="case-closed-at-date-error"
                state={FormState.ERROR}
                helpText="Please enter a valid date"
              />
            )}
          </DatePickerContainer>
        </CaseDateRow>
      </CaseDatesPanelContainer>
    </CaseScreenHeaderDetailsColumnNew>
  );
};
